import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { 
  Plus, 
  Search, 
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  FileText,
  Building2,
  Calendar,
  DollarSign,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Send,
  Eye,
  TrendingUp,
  Award,
  Briefcase
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const Grants = () => {
  const [grants, setGrants] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editingGrant, setEditingGrant] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    agency: "",
    grant_type: "",
    amount_requested: "",
    description: "",
    deadline: "",
    program_officer: "",
    program_officer_email: "",
    cfda_number: "",
    notes: ""
  });

  const grantTypes = [
    { value: "federal", label: "Federal", color: "bg-blue-100 text-blue-800 border-blue-200" },
    { value: "state", label: "State", color: "bg-purple-100 text-purple-800 border-purple-200" },
    { value: "local", label: "Local", color: "bg-green-100 text-green-800 border-green-200" },
    { value: "foundation", label: "Foundation", color: "bg-amber-100 text-amber-800 border-amber-200" },
    { value: "corporate", label: "Corporate", color: "bg-pink-100 text-pink-800 border-pink-200" }
  ];

  const grantStatuses = [
    { value: "draft", label: "Draft", color: "bg-gray-100 text-gray-800 border-gray-200", icon: FileText },
    { value: "submitted", label: "Submitted", color: "bg-blue-100 text-blue-800 border-blue-200", icon: Send },
    { value: "under_review", label: "Under Review", color: "bg-amber-100 text-amber-800 border-amber-200", icon: Eye },
    { value: "approved", label: "Approved", color: "bg-green-100 text-green-800 border-green-200", icon: CheckCircle2 },
    { value: "denied", label: "Denied", color: "bg-red-100 text-red-800 border-red-200", icon: XCircle },
    { value: "awarded", label: "Awarded", color: "bg-emerald-100 text-emerald-800 border-emerald-200", icon: Award }
  ];

  useEffect(() => {
    fetchGrants();
  }, [statusFilter, typeFilter]);

  const fetchGrants = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append("status", statusFilter);
      if (typeFilter) params.append("grant_type", typeFilter);
      if (search) params.append("search", search);
      
      const response = await axios.get(`${API}/grants?${params}`);
      setGrants(response.data.items || []);
      setSummary(response.data.summary || {});
    } catch (error) {
      toast.error("Failed to fetch grants");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchGrants();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        amount_requested: parseFloat(formData.amount_requested)
      };
      
      if (editingGrant) {
        await axios.put(`${API}/grants/${editingGrant.id}`, payload);
        toast.success("Grant updated successfully");
      } else {
        await axios.post(`${API}/grants`, payload);
        toast.success("Grant created successfully");
      }
      setShowDialog(false);
      resetForm();
      fetchGrants();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save grant");
    }
  };

  const handleEdit = (grant) => {
    setEditingGrant(grant);
    setFormData({
      title: grant.title,
      agency: grant.agency,
      grant_type: grant.grant_type,
      amount_requested: grant.amount_requested.toString(),
      description: grant.description,
      deadline: grant.deadline,
      program_officer: grant.program_officer || "",
      program_officer_email: grant.program_officer_email || "",
      cfda_number: grant.cfda_number || "",
      notes: grant.notes || ""
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this grant?")) return;
    try {
      await axios.delete(`${API}/grants/${id}`);
      toast.success("Grant deleted");
      fetchGrants();
    } catch (error) {
      toast.error("Failed to delete grant");
    }
  };

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      const updates = { status: newStatus };
      if (newStatus === "submitted") {
        updates.submission_date = new Date().toISOString().split('T')[0];
      }
      await axios.put(`${API}/grants/${id}`, updates);
      toast.success(`Grant status updated to ${newStatus}`);
      fetchGrants();
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const resetForm = () => {
    setEditingGrant(null);
    setFormData({
      title: "",
      agency: "",
      grant_type: "",
      amount_requested: "",
      description: "",
      deadline: "",
      program_officer: "",
      program_officer_email: "",
      cfda_number: "",
      notes: ""
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDaysUntilDeadline = (deadline) => {
    if (!deadline) return null;
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStatusBadge = (status) => {
    const config = grantStatuses.find(s => s.value === status) || grantStatuses[0];
    const Icon = config.icon;
    return (
      <Badge variant="outline" className={`${config.color} gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  const getTypeBadge = (type) => {
    const config = grantTypes.find(t => t.value === type) || grantTypes[0];
    return (
      <Badge variant="outline" className={config.color}>
        {config.label}
      </Badge>
    );
  };

  // Calculate stats
  const totalRequested = summary.total_requested || 0;
  const totalAwarded = summary.total_awarded || 0;
  const successRate = summary.by_status?.awarded && summary.total_grants 
    ? ((summary.by_status.awarded / summary.total_grants) * 100).toFixed(0)
    : 0;

  return (
    <div className="space-y-6" data-testid="grants-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
            Grant Tracking
          </h1>
          <p className="text-slate-500 mt-1">Manage funding applications and track outcomes</p>
        </div>
        <div className="flex gap-3">
          <Link to="/dol-proposal">
            <Button variant="outline" className="gap-2" data-testid="view-dol-proposal">
              <FileText className="w-4 h-4" />
              DOL Proposal
            </Button>
          </Link>
          <Button 
            className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
            onClick={() => { resetForm(); setShowDialog(true); }}
            data-testid="add-grant-btn"
          >
            <Plus className="w-4 h-4" />
            New Grant
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="dashboard-widget stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Briefcase className="w-6 h-6 text-blue-600" />
              </div>
              <span className="text-xs text-blue-600 font-medium">Total</span>
            </div>
            <div className="mt-4">
              <div className="text-2xl font-bold text-slate-900">{summary.total_grants || 0}</div>
              <div className="text-sm text-slate-500">Total Grants</div>
            </div>
          </CardContent>
        </Card>

        <Card className="dashboard-widget stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-amber-600" />
              </div>
              <span className="text-xs text-amber-600 font-medium">Requested</span>
            </div>
            <div className="mt-4">
              <div className="text-2xl font-bold text-slate-900">TBD</div>
              <div className="text-sm text-slate-500">Total Requested</div>
            </div>
          </CardContent>
        </Card>

        <Card className="dashboard-widget stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                <Award className="w-6 h-6 text-green-600" />
              </div>
              <span className="text-xs text-green-600 font-medium">Awarded</span>
            </div>
            <div className="mt-4">
              <div className="text-2xl font-bold text-green-600">TBD</div>
              <div className="text-sm text-slate-500">Total Awarded</div>
            </div>
          </CardContent>
        </Card>

        <Card className="dashboard-widget stat-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
              <span className="text-xs text-purple-600 font-medium">Rate</span>
            </div>
            <div className="mt-4">
              <div className="text-2xl font-bold text-slate-900">{successRate}%</div>
              <div className="text-sm text-slate-500">Success Rate</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Funding Progress */}
      <Card className="dashboard-widget">
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Funding Pipeline</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">Awarded vs Requested</span>
              <span className="font-medium">{totalRequested > 0 ? ((totalAwarded / totalRequested) * 100).toFixed(1) : 0}%</span>
            </div>
            <Progress 
              value={totalRequested > 0 ? (totalAwarded / totalRequested) * 100 : 0} 
              className="h-3"
            />
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mt-4">
              {grantStatuses.map(status => {
                const count = summary.by_status?.[status.value] || 0;
                return (
                  <div key={status.value} className="text-center">
                    <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs ${status.color}`}>
                      <status.icon className="w-3 h-3" />
                      {status.label}
                    </div>
                    <div className="text-lg font-bold mt-1">{count}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <form onSubmit={handleSearch} className="flex-1 flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search grants by title or agency..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="search-input"
                />
              </div>
              <Button type="submit" variant="secondary">Search</Button>
            </form>
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[160px]" data-testid="status-filter">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {grantStatuses.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={typeFilter || "all"} onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[140px]" data-testid="type-filter">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {grantTypes.map(t => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Grants Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Grant</TableHead>
                <TableHead>Agency</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Amount</TableHead>
                <TableHead>Deadline</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8">
                    <div className="animate-pulse">Loading...</div>
                  </TableCell>
                </TableRow>
              ) : grants.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    No grants found
                  </TableCell>
                </TableRow>
              ) : (
                grants.map((grant) => {
                  const daysUntil = getDaysUntilDeadline(grant.deadline);
                  const isUrgent = daysUntil !== null && daysUntil <= 14 && daysUntil > 0 && grant.status === "draft";
                  
                  return (
                    <TableRow key={grant.id} data-testid={`grant-row-${grant.id}`} className={isUrgent ? "bg-red-50" : ""}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-blue-100 rounded-lg flex items-center justify-center">
                            <Building2 className="w-4 h-4 text-blue-600" />
                          </div>
                          <div>
                            <div className="font-medium text-slate-900">{grant.title}</div>
                            {grant.cfda_number && (
                              <div className="text-xs text-slate-500">CFDA: {grant.cfda_number}</div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-slate-600">{grant.agency}</TableCell>
                      <TableCell>{getTypeBadge(grant.grant_type)}</TableCell>
                      <TableCell>
                        <div className="font-medium">{formatCurrency(grant.amount_requested)}</div>
                        {grant.amount_awarded > 0 && (
                          <div className="text-xs text-green-600">Awarded: {formatCurrency(grant.amount_awarded)}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 text-slate-400" />
                          <span className={isUrgent ? "text-red-600 font-medium" : "text-slate-600"}>
                            {formatDate(grant.deadline)}
                          </span>
                        </div>
                        {daysUntil !== null && daysUntil > 0 && grant.status === "draft" && (
                          <div className={`text-xs mt-1 ${isUrgent ? "text-red-600" : "text-slate-500"}`}>
                            {daysUntil} days left
                          </div>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(grant.status)}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" data-testid={`actions-${grant.id}`}>
                              <MoreHorizontal className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEdit(grant)}>
                              <Edit className="w-4 h-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            {grant.status === "draft" && (
                              <DropdownMenuItem onClick={() => handleStatusUpdate(grant.id, "submitted")}>
                                <Send className="w-4 h-4 mr-2 text-blue-600" /> Mark Submitted
                              </DropdownMenuItem>
                            )}
                            {grant.status === "submitted" && (
                              <DropdownMenuItem onClick={() => handleStatusUpdate(grant.id, "under_review")}>
                                <Eye className="w-4 h-4 mr-2 text-amber-600" /> Under Review
                              </DropdownMenuItem>
                            )}
                            {(grant.status === "under_review" || grant.status === "submitted") && (
                              <>
                                <DropdownMenuItem onClick={() => handleStatusUpdate(grant.id, "approved")}>
                                  <CheckCircle2 className="w-4 h-4 mr-2 text-green-600" /> Approved
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleStatusUpdate(grant.id, "denied")}>
                                  <XCircle className="w-4 h-4 mr-2 text-red-600" /> Denied
                                </DropdownMenuItem>
                              </>
                            )}
                            {grant.status === "approved" && (
                              <DropdownMenuItem onClick={() => handleStatusUpdate(grant.id, "awarded")}>
                                <Award className="w-4 h-4 mr-2 text-emerald-600" /> Mark Awarded
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuSeparator />
                            <DropdownMenuItem 
                              onClick={() => handleDelete(grant.id)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingGrant ? "Edit Grant" : "Add New Grant"}
            </DialogTitle>
            <DialogDescription>
              {editingGrant ? "Update grant application details" : "Enter details for a new grant opportunity"}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-900">Grant Information</h4>
              <div>
                <Label htmlFor="title">Grant Title *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  placeholder="e.g., DOL Workforce Development Grant FY2026"
                  required
                  data-testid="input-title"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="agency">Funding Agency *</Label>
                  <Input
                    id="agency"
                    value={formData.agency}
                    onChange={(e) => setFormData({...formData, agency: e.target.value})}
                    placeholder="e.g., U.S. Department of Labor"
                    required
                    data-testid="input-agency"
                  />
                </div>
                <div>
                  <Label htmlFor="grant_type">Grant Type *</Label>
                  <Select 
                    value={formData.grant_type} 
                    onValueChange={(v) => setFormData({...formData, grant_type: v})}
                  >
                    <SelectTrigger data-testid="select-type">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {grantTypes.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="amount_requested">Amount Requested ($) *</Label>
                  <Input
                    id="amount_requested"
                    type="number"
                    min="0"
                    step="1000"
                    value={formData.amount_requested}
                    onChange={(e) => setFormData({...formData, amount_requested: e.target.value})}
                    required
                    data-testid="input-amount"
                  />
                </div>
                <div>
                  <Label htmlFor="deadline">Application Deadline *</Label>
                  <Input
                    id="deadline"
                    type="date"
                    value={formData.deadline}
                    onChange={(e) => setFormData({...formData, deadline: e.target.value})}
                    required
                    data-testid="input-deadline"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Brief description of the grant opportunity and intended use"
                  required
                  data-testid="input-description"
                />
              </div>
            </div>

            {/* Contact Info */}
            <div className="space-y-4">
              <h4 className="font-medium text-slate-900">Program Officer Contact</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="program_officer">Program Officer Name</Label>
                  <Input
                    id="program_officer"
                    value={formData.program_officer}
                    onChange={(e) => setFormData({...formData, program_officer: e.target.value})}
                    placeholder="e.g., Dr. Sarah Mitchell"
                    data-testid="input-officer"
                  />
                </div>
                <div>
                  <Label htmlFor="program_officer_email">Program Officer Email</Label>
                  <Input
                    id="program_officer_email"
                    type="email"
                    value={formData.program_officer_email}
                    onChange={(e) => setFormData({...formData, program_officer_email: e.target.value})}
                    placeholder="e.g., sarah.mitchell@dol.gov"
                    data-testid="input-officer-email"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="cfda_number">CFDA Number (if federal)</Label>
                <Input
                  id="cfda_number"
                  value={formData.cfda_number}
                  onChange={(e) => setFormData({...formData, cfda_number: e.target.value})}
                  placeholder="e.g., 17.XXX"
                  data-testid="input-cfda"
                />
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                placeholder="Additional notes, requirements, or reminders..."
                data-testid="input-notes"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-blue-600 hover:bg-blue-700" data-testid="submit-btn">
                {editingGrant ? "Update" : "Create"} Grant
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Grants;
